import type { FC, PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { isDjangoErrorResponse } from 'api/common';

export const WithQueryClient: FC<PropsWithChildren> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchInterval: false,
            refetchIntervalInBackground: false,
            retryOnMount: false,
            retry: (failureCount, error) => {
              if (isDjangoErrorResponse(error) && error.code === 'approve_required') {
                // we don't want to retry if the user is not approved
                return false;
              }

              if (import.meta.env.DEV) {
                return false;
              }

              return failureCount < 3;
            },
          },
        },
        mutationCache: new MutationCache(),
        queryCache: new QueryCache({
          onError: async (error) => {
            // if we are getting approve_required from any request when there is mismatch between user and company (for instance, user has been rejected but old company is still chosen)
            if (isDjangoErrorResponse(error) && error.code === 'approve_required') {
              enqueueSnackbar('Your account is not approved in this company. Please choose another company.', {
                preventDuplicate: true,
                key: 'approve_required',
              });
            }
          },
        }),
      }),
    [enqueueSnackbar],
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};
